import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Users, User } from 'lucide-react';

import { authAPI } from '../../services/api';
import { saveAuthData } from '../../utils/auth';
import { Alert } from '../common/Alert';
import { Input } from '../common/Input';
import { Button } from '../common/Button';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSupplierError, setIsSupplierError] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setIsSupplierError(false);

    try {
      const response = await authAPI.login(email, password);
      
      // Check if email verification is required
      if ('requiresVerification' in response) {
        setError(response.message || 'Please verify your email before logging in');
        return;
      }
      
      // Check if user is admin
      if (response.user.userType !== 'admin') {
        setError('Access denied. This login is for administrators only.');
        setIsSupplierError(true);
        return;
      }
      
      // Save auth data and navigate (TypeScript now knows this is AuthData)
      saveAuthData(response);
      navigate('/admin');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#63B56F] to-[#193B4E] py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-2xl"
      >
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Admin Sign In
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Access your solar panel management dashboard
          </p>
        </div>

        {error && <Alert type="error" message={error} onClose={() => setError('')} />}

        {isSupplierError && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-3">Login to the correct portal:</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                onClick={() => navigate('/supplier/login')}
                variant="secondary"
                className="w-full flex items-center justify-center"
              >
                <Users className="h-4 w-4 mr-2" />
                Supplier Login
              </Button>
              <Button
                onClick={() => navigate('/user/login')}
                variant="secondary"
                className="w-full flex items-center justify-center"
              >
                <User className="h-4 w-4 mr-2" />
                User Login
              </Button>
            </div>
          </div>
        )}

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10"
                required
              />
              <button
                type="button"
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            size="lg"
            loading={loading}
          >
            Sign In
          </Button>

          <div className="text-center mt-4">
            <Link
              to="/forgot-password"
              className="text-sm text-[#63B56F] hover:text-[#4A9B57]"
            >
              Forgot your password?
            </Link>
          </div>
        </form>

        {/* Navigation buttons for other login types */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-center text-sm text-gray-600 mb-4">
            Looking for a different login?
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              onClick={() => navigate('/supplier/login')}
              variant="secondary"
              className="w-full flex items-center justify-center text-sm py-2"
            >
              <Users className="h-4 w-4 mr-2" />
              Supplier Login
            </Button>
            <Button
              onClick={() => navigate('/user/login')}
              variant="secondary"
              className="w-full flex items-center justify-center text-sm py-2"
            >
              <User className="h-4 w-4 mr-2" />
              User Login
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};